import { getAccessToken, refreshToken } from "Helpers/api";

let checkURL = "";
const urlhost = window.location.host;
if (urlhost.includes('ev.')) {
  checkURL = 'https://api.phishing-manager.dev.venzagrc.com';
}
if (urlhost.includes('tg.')) {
  checkURL = 'https://api.phishing-manager.stg.venzagrc.com';
}
if (!urlhost.includes('ev.') && !urlhost.includes('tg.') && !urlhost.includes(':8')) {
  checkURL = 'https://api.phishing-manager.prod.venzagrc.com';
}

const baseUrl = checkURL as string;

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${getAccessToken()}`,
});

export interface CreateCampaignStepOnePayload {
  venza_tenant_id: number;
  packaged_template_id: number;
  language_id: number;
  name?: string;
  campaign_id?: number;
}


const authorizedFetch = async (
  url: string,
  options: RequestInit,
  retryCount = 0
): Promise<Response> => {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${getAccessToken()}`,
    },
  });

  if (response.status === 401 && retryCount < 3) {
    try {
      await refreshToken();
      return authorizedFetch(url, options, retryCount + 1);
    } catch (err) {
      console.error("Token refresh failed:", err);
      throw err;
    }
  }
  return response;
};

export const getPhishingCampaignsForTenant = async (
  tenantId: string | number | undefined,
  page: number
) => {
  const url = `${baseUrl}/public/campaigns?venza_tenant_id=${tenantId}&page=${page}`;
  try {
    const response = await authorizedFetch(url, { method: "GET", headers: getAuthHeaders() });
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching provider list:", error);
    throw error;
  }
};

export const getPhishingCampaignsTemplatesForTenant = async (
  tenantId: string
) => {
  const url = `${baseUrl}/public/campaigns?venza_tenant_id=${tenantId}`;
  try {
    const response = await authorizedFetch(url, { method: "GET", headers: getAuthHeaders() });
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching provider list:", error);
    throw error;
  }
};

export const getPhishingCampaignData = async (
  tenantId: string
) => {
  const url = `${baseUrl}/public/tenants/${tenantId}/campaigns/threshold-summary`;
  try {
    const response = await authorizedFetch(url, { method: "GET", headers: getAuthHeaders() });
    if (!response.ok) {
      const errorBody = await response.json();
      throw {
        status: response.status,
        data: errorBody,
      };
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching provider list:", error);
    throw error;
  }
};

export const getPhishingCampaignFilters = async (
  tenantId: string | number | undefined,
  value: string
) => {
  let url = `${baseUrl}/public/packaged-templates/filters?venza_tenant_id=${tenantId}`;
  if (!!value) {
    url = `${baseUrl}/public/packaged-templates/filters?venza_tenant_id=${tenantId}&filter=${value}`;
  }
  try {
    const response = await authorizedFetch(url, { method: "GET", headers: getAuthHeaders() });
    if (!response.ok) {
      const errorBody = await response.json();
      throw {
        status: response.status,
        data: errorBody,
      };
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching provider list:", error);
    throw error;
  }
};

export const getPhishingCampaignTemplates = async (
  tenantId: string | number | undefined,
  params: {
    name?: string;
    difficulties?: string[];
    languages?: number[];
    categories?: number[];
    page?: number;
    limit?: number;
  }
) => {
  if (!tenantId) return;

  const query = new URLSearchParams();

  if (params.name) query.append("name", params.name);

  params.difficulties?.forEach(d =>
    query.append("difficulties[]", d.toLowerCase())
  );

  params.languages?.forEach(l =>
    query.append("languages[]", String(l))
  );

  params.categories?.forEach(c =>
    query.append("categories[]", String(c))
  );

  if (params.page) query.append("page", String(params.page));
  if (params.limit) query.append("limit", String(params.limit));
  const url = `${baseUrl}/public/packaged-templates?venza_tenant_id=${tenantId}&${query.toString()}`;

  const response = await authorizedFetch(url, {
    method: "GET",
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const errorBody = await response.json();
    throw errorBody;
  }

  return response.json();
};

export const getPhishingCampaignTemplatePreviewDetails = async (
  tenantId: string | number | undefined,
  templateId?: string | number | null,
  packagedTemplateLanguageId?: string | number | null,
) => {
  if (!tenantId || !templateId) return;

  const query = new URLSearchParams();
  query.append("venza_tenant_id", String(tenantId));

  if (packagedTemplateLanguageId) {
    query.append(
      "packaged_template_language_id",
      String(packagedTemplateLanguageId)
    );
  }

  const url = `${baseUrl}/public/packaged-templates/preview/${templateId}?${query.toString()}`;

  const response = await authorizedFetch(url, {
    method: "GET",
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const errorBody = await response.json();
    throw errorBody;
  }

  return response.json();
};

export const createPhishingCampaignStepOne = async (
  payload: CreateCampaignStepOnePayload
) => {
  const {
    venza_tenant_id,
    packaged_template_id,
    language_id,
    campaign_id,
    name,
  } = payload;

  const url = `${baseUrl}/public/campaigns/create/step-1`;

  const response = await authorizedFetch(url, {
    method: "POST",
    headers: {
      ...getAuthHeaders(),
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      venza_tenant_id,
      packaged_template_id,
      language_id,
      campaign_id,
      ...(name ? { name } : {}),
    }),
  });

  if (!response.ok) {
    const errorBody = await response.json();
    throw errorBody;
  }

  return response.json();
};

export interface CreateCampaignStepTwoPayload {
  venza_tenant_id: number;
  campaign_id: number;
  emails: string[];
}

export const createPhishingCampaignStepTwo = async (
  payload: CreateCampaignStepTwoPayload
) => {
  const { venza_tenant_id, campaign_id, emails } = payload;

  if (!emails || emails.length === 0) return;

  const query = new URLSearchParams();
  query.append("venza_tenant_id", String(venza_tenant_id));
  query.append("campaign_id", String(campaign_id));

  emails.forEach((email) => {
    query.append("emails[]", email);
  });

  const url = `${baseUrl}/public/campaigns/create/step-2?${query.toString()}`;

  const response = await authorizedFetch(url, {
    method: "POST",
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const errorBody = await response.json();
    throw errorBody;
  }

  return response.json();
};

export const launchPhishingCampaign = async (
  payload: any
) => {
  
  const { venza_tenant_id, campaign_id } = payload;
  const query = new URLSearchParams();
  query.append("venza_tenant_id", String(venza_tenant_id));
  query.append("campaign_id", String(campaign_id));

  const url = `${baseUrl}/public/campaigns/create/start-test?${query.toString()}`;

  const response = await authorizedFetch(url, {
    method: "POST",
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const errorBody = await response.json();
    throw errorBody;
  }

  return response.json();
};

export const getPhisingTimeZones = async (tenantId: string | number | undefined) => {
  const url = `${baseUrl}/public/timezones?venza_tenant_id=${tenantId}`;
  try {
    const response = await authorizedFetch(url, { method: "GET", headers: getAuthHeaders() });
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching provider list:", error);
    throw error;
  }
};

export const submitStep4 = async (
  payload: any,
  tenantId: string | number | undefined
) => {
  const url = `${baseUrl}/public/campaigns/create/step-4?venza_tenant_id=${tenantId}`;

  const response = await authorizedFetch(url, {
    method: "POST",
    headers: {
      ...getAuthHeaders(),
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorBody = await response.json();
    throw errorBody;
  }

  return response.json();
};